import React, { useState, useEffect, useMemo } from 'react';
import { Modal } from '@/components/modal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RotateCcw } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  getIconComponent,
  iconMap,
  type IconName,
} from '@/pages/workflow/helper/icon-mapping';
import { COLOR_OPTIONS } from '@/constants/node-styling';
import { useNodeStore } from '@/stores/node-store';
import { findCatalogStruct } from '@/stores/helpers/node-catalog-lookup';

interface NodeAttrEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (updates: {
    user_config_name?: string;
    user_config_description?: string;
    icon?: string;
    icon_color?: string;
  }) => void;
  nodeName: string;
  nodeDescription?: string;
  nodeIcon?: string;
  nodeIconColor?: string;
  userConfigName?: string;
  userConfigDescription?: string;
  nodeNameId: string; // node_id from struct, renamed to avoid confusion with React Flow ID
}

export const NodeAttrEditModal: React.FC<NodeAttrEditModalProps> = ({
  isOpen,
  onClose,
  onSave,
  nodeName,
  nodeDescription,
  nodeIcon,
  nodeIconColor,
  userConfigName,
  userConfigDescription,
  nodeNameId,
}) => {
  const groupedNodes = useNodeStore((state) => state.groupedNodes);

  // Find original node definition from the store
  const originalNode = useMemo(
    () => findCatalogStruct(groupedNodes, nodeNameId),
    [groupedNodes, nodeNameId],
  );

  const [name, setName] = useState(userConfigName || nodeName);
  const [description, setDescription] = useState(
    userConfigDescription || nodeDescription || '',
  );
  const [icon, setIcon] = useState(nodeIcon || '');
  const [iconColor, setIconColor] = useState(nodeIconColor || 'default');
  const [nameError, setNameError] = useState<string | null>(null);
  const [descriptionError, setDescriptionError] = useState<string | null>(null);

  // Dynamically get all available icon names from iconMap
  const iconNames = useMemo(
    () => (Object.keys(iconMap) as IconName[]).sort(),
    [],
  );

  // Update form when props change
  useEffect(() => {
    setName(userConfigName || nodeName);
    setDescription(userConfigDescription || nodeDescription || '');
    setIcon(nodeIcon || '');
    setIconColor(nodeIconColor || 'default');
    setNameError(null);
    setDescriptionError(null);
  }, [
    nodeName,
    nodeDescription,
    nodeIcon,
    nodeIconColor,
    userConfigName,
    userConfigDescription,
    isOpen,
  ]);

  const handleSave = () => {
    const trimmedName = name.trim();
    const trimmedDescription = description.trim();

    // Validate required fields
    let hasError = false;

    if (!trimmedName) {
      setNameError('Name is required');
      hasError = true;
    } else {
      setNameError(null);
    }

    if (!trimmedDescription) {
      setDescriptionError('Description is required');
      hasError = true;
    } else {
      setDescriptionError(null);
    }

    if (hasError) {
      return;
    }

    onSave({
      user_config_name: trimmedName || undefined,
      user_config_description: trimmedDescription,
      icon: icon.trim() || undefined,
      icon_color:
        iconColor === 'default' ? undefined : iconColor.trim() || undefined,
    });
  };

  const handleCancel = () => {
    // Reset to original values
    setName(userConfigName || nodeName);
    setDescription(userConfigDescription || nodeDescription || '');
    setIcon(nodeIcon || '');
    setIconColor(nodeIconColor || 'default');
    setNameError(null);
    setDescriptionError(null);
    onClose();
  };

  const handleResetToDefault = () => {
    // Reset to default values (node's original display_name and description)
    // Fetch original icon from the node definition in the store
    const originalIcon = originalNode?.icon || nodeIcon || '';

    onSave({
      user_config_name: '',
      user_config_description: '',
      icon: originalIcon,
      icon_color: '',
    });
  };

  return (
    <Modal
      title='Edit Node Attributes'
      isDialogOpen={isOpen}
      setIsDialogOpen={(open) => {
        if (!open) {
          handleCancel();
        }
      }}
      footer={
        <div
          className='flex items-center w-full'
          onClick={(e) => e.stopPropagation()}
        >
          <Button
            variant='ghost'
            onClick={handleResetToDefault}
            className='mr-auto'
          >
            <RotateCcw /> Reset to Default
          </Button>
          <div className='flex gap-2'>
            <Button variant='outline' onClick={handleCancel}>
              Cancel
            </Button>
            <Button onClick={handleSave}>Save</Button>
          </div>
        </div>
      }
      contentClassName='px-6 py-4'
    >
      <div className='space-y-4 my-4'>
        <div className='space-y-2'>
          <Label htmlFor='node-name' className='label-required'>
            Name
          </Label>
          <Input
            id='node-name'
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (nameError) setNameError(null);
            }}
            placeholder='Enter node name'
            aria-invalid={!!nameError}
            className={nameError ? 'border-destructive' : ''}
          />
          {nameError && <p className='text-sm text-destructive'>{nameError}</p>}
        </div>

        <div className='space-y-2'>
          <Label htmlFor='node-description' className='label-required'>
            Description
          </Label>
          <Textarea
            id='node-description'
            value={description}
            onChange={(e) => {
              setDescription(e.target.value);
              if (descriptionError) setDescriptionError(null);
            }}
            placeholder='Enter node description'
            rows={4}
            aria-invalid={!!descriptionError}
            className={descriptionError ? 'border-destructive' : ''}
          />
          {descriptionError && (
            <p className='text-sm text-destructive'>{descriptionError}</p>
          )}
        </div>

        <div className='space-y-2'>
          <Label>Thumbnail</Label>
          <div className='flex items-start gap-1 w-full'>
            <div className='w-fit'>
              <Select value={icon || ''} onValueChange={setIcon}>
                <SelectTrigger id='node-icon'>
                  <SelectValue placeholder='Select an icon' />
                </SelectTrigger>
                <SelectContent className='max-h-64'>
                  {iconNames.map((iconName) => (
                    <SelectItem key={iconName} value={iconName}>
                      <div className='flex items-center gap-2'>
                        <span className='flex items-center justify-center w-4 h-4 shrink-0'>
                          {getIconComponent(iconName, undefined, 'w-4 h-4')}
                        </span>
                        <span>{iconName}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className='w-fit'>
              <Select
                value={iconColor || 'default'}
                onValueChange={setIconColor}
              >
                <SelectTrigger id='icon-color'>
                  <SelectValue placeholder='Select a color' />
                </SelectTrigger>
                <SelectContent>
                  {COLOR_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      <div className='flex items-center gap-2'>
                        {option.color ? (
                          <div
                            className='w-2 h-2 rounded-full shrink-0'
                            style={{ backgroundColor: option.color }}
                          />
                        ) : (
                          <div
                            className='w-2 h-2 rounded-full border border-gray-900 dark:border-gray-300 shrink-0'
                            style={{
                              background:
                                'linear-gradient(120deg, black 50%, white 50%)',
                            }}
                          />
                        )}
                        <span>{option.label}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};
